/**
 * BLOK-OMBOUW — STAP 1 prototype: blok-module (pure functies).
 *
 * Model: doorlopende 4-weken-blokken, anker op ISO-week 1 van het jaar.
 *   blok 1 = week 1-4, blok 2 = week 5-8, ... blok 13 = week 49-52.
 *
 * RANDGEVAL W53 (o.a. 2026): week 53 past niet in het 13-blokken-raster.
 * Voorlopige keuze: week 53 is de 5e week van blok 13 (week 49-53).
 * Overal waar dat speelt wordt het expliciet gemarkeerd (bevatWeek53 /
 * isWeek53) zodat het randgeval beoordeeld kan worden vóór definitieve keuze.
 */
import { maandWeken, isoWeekNummer } from './maand.js';

/** Heeft dit ISO-jaar 53 weken? (de week van 28 december is per definitie de laatste) */
export function heeftWeek53(isoJaar) {
  return isoWeekNummer(new Date(isoJaar, 11, 28)).week === 53;
}

/** Bloknummer (1-13) voor een ISO-weeknummer. Week 53 -> blok 13 (randgeval). */
export function blokNummer(week) {
  if (!Number.isInteger(week) || week < 1 || week > 53) {
    throw new Error(`ongeldig weeknummer: ${week}`);
  }
  return Math.min(13, Math.ceil(week / 4));
}

/**
 * De weeknummers van een blok. Standaard 4 weken; alleen blok 13 krijgt in een
 * W53-jaar (heeft53=true) week 53 als 5e week erbij.
 */
export function blokWeken(bloknummer, heeft53 = false) {
  if (!Number.isInteger(bloknummer) || bloknummer < 1 || bloknummer > 13) {
    throw new Error(`ongeldig bloknummer: ${bloknummer}`);
  }
  const weken = [bloknummer * 4 - 3, bloknummer * 4 - 2, bloknummer * 4 - 1, bloknummer * 4];
  if (bloknummer === 13 && heeft53) weken.push(53); // RANDGEVAL: 5e week van blok 13
  return weken;
}

/**
 * Maand (Regel B) -> de maand-weken plus de COMPLETE blokken die nodig zijn om
 * al die weken te dekken. Blokken zijn jaar-gebonden (anker = ISO-week 1 van
 * het eigen isoJaar); een maand-venster kan blokken uit één of twee ISO-jaren
 * raken (bv. december met week 1 van het volgende isoJaar erin).
 *
 * -> {
 *      maandWeken: [{ week, isoJaar, maandag }],   // zoals maandWeken() ze geeft
 *      aantal,                                     // aantal maand-weken (4/5)
 *      blokken: [{
 *        blok, isoJaar,
 *        weken,            // alle weken van het complete blok (4, of 5 bij W53)
 *        inMaand,          // deelverzameling die in de maand valt
 *        extraWeken,       // blok-weken BUITEN de maand (het "kijkvenster-overschot")
 *        bevatWeek53,      // randgeval-markering
 *      }]
 *    }
 */
export function blokkenVoorMaand(jaar, maand) {
  const { weken, aantal } = maandWeken(jaar, maand);

  // unieke (isoJaar, blok)-paren in venster-volgorde
  const volgorde = [];
  const gezien = new Set();
  for (const w of weken) {
    const key = `${w.isoJaar}:${blokNummer(w.week)}`;
    if (!gezien.has(key)) {
      gezien.add(key);
      volgorde.push({ isoJaar: w.isoJaar, blok: blokNummer(w.week) });
    }
  }

  const inMaandSet = new Set(weken.map(w => `${w.isoJaar}:${w.week}`));
  const blokken = volgorde.map(({ isoJaar, blok }) => {
    const blokWk = blokWeken(blok, heeftWeek53(isoJaar));
    return {
      blok,
      isoJaar,
      weken: blokWk,
      inMaand: blokWk.filter(wk => inMaandSet.has(`${isoJaar}:${wk}`)),
      extraWeken: blokWk.filter(wk => !inMaandSet.has(`${isoJaar}:${wk}`)),
      bevatWeek53: blokWk.includes(53),
    };
  });

  return { maandWeken: weken, aantal, blokken };
}
